<template>
  <v-dialog :value="dialogProp.show" persistent max-width="900">
    <v-card class="elevation-12">
      <v-toolbar dense :dark="$vuetify.theme.dark" color="primary">
        <v-toolbar-title>
          {{ $t("Подача ценового предложения") }}
        </v-toolbar-title>
      </v-toolbar>
      <v-row v-if="!!generalError || innerLoading" class="my-0 py-0 mx-1">
        <v-col v-if="innerLoading" cols="12">
          <v-progress-linear
              :active="innerLoading"
              color="loader"
              indeterminate />
        </v-col>
        <v-col v-if="!!generalError" class="mx-1">
          <general-error v-if="!!generalError" />
        </v-col>
      </v-row>
      <v-alert
          class="text-center ma-1"
          outlined
          :value="success"
          type="success">
        {{ $t("Заявка успешно внесена") }}
      </v-alert>
      <v-card-text class="pt-8">
        <ValidationObserver ref="order">
          <v-data-table
              :headers="headers"
              :items="order.premiums || []"
              :items-per-page="-1"
              item-key="positionId"
              disable-sort-by
              :locale="systemLocale"
              hide-default-footer
              dense>
            <!-- eslint-disable-next-line  vue/no-template-shadow, vue/valid-v-slot -->
            <template v-slot:item.positionDescription="{ item }">
              <span :title="item.positionDescription">
                {{
                  item.positionDescription &&
                    item.positionDescription.length > 50
                    ? `${item.positionDescription.substring(0, 50)}...`
                    : item.positionDescription
                }}
              </span>
            </template>
            <!-- eslint-disable-next-line  vue/no-template-shadow, vue/valid-v-slot -->
            <template v-slot:item.premium="{ item }">
              <v-row class="pb-1 pt-0 my-0">
                <v-col class="py-0 my-0">
                  <validatable-text-input
                      :id="item.positionId"
                      v-model="item.premium"
                      :vid="item.positionId"
                      :readonly="innerLoading"
                      :rules="{
                        required: true,
                        max_value: dialogProp.isFixPremium
                          ? MAX_FIX_PREMIUM_VALUE
                          : MAX_DIFF_PREMIUM_VALUE,
                        min_value: dialogProp.isFixPremium
                          ? MIN_FIX_PREMIUM_VALUE
                          : MIN_DIFF_PREMIUM_VALUE,
                        regex: /^(0|[1-9]\d*)$/
                      }"
                      single-line
                      :label="premiumLabel"
                      hide-details="auto"
                      :tabindex="1"
                      solo
                      dense
                      data-type="float" />
                </v-col>
              </v-row>
            </template>
          </v-data-table>
          <v-row v-if="dialogProp.showAdditionalInfo" class="py-0 my-1">
            <v-col class="py-0 my-0">
              <validatable-textarea
                  v-model="order.additionalInfo"
                  :tabindex="3"
                  :readonly="isLoading"
                  :rules="{ max: 2000 }"
                  :label="$t('Дополнительные условия')" />
            </v-col>
          </v-row>
          <v-row class="pb-1 pt-0 my-0">
            <v-col cols="12" md="12" class="pl-2">
              <validatable-checkbox
                  v-model="isClient"
                  :label="$t('Клиентская')"
                  @change="clearClientInfo" />
            </v-col>
          </v-row>
          <v-row class="pb-1 pt-0 my-0">
            <v-col cols="12" md="6" class="pl-2">
              <validatable-dynamic-text-input
                  v-if="isClient"
                  v-model="order.clientOrganizationInn"
                  v-test="{ input: 'clientOrganizationInn' }"
                  :readonly="!isClient"
                  rules="required"
                  dense
                  :label="$t('ИНН организации Клиента')" />
            </v-col>
            <v-col cols="12" md="6" class="pl-2">
              <validatable-dynamic-text-input
                  v-if="isClient"
                  v-model="order.clientOrganizationFullName"
                  v-test="{ input: 'clientOrganizationFullName' }"
                  rules="required"
                  dense
                  :readonly="!isClient"
                  :label="$t('Полное наименование организации Клиента')" />
            </v-col>
            <v-col v-if="isClient">
              <text-view
                  cols="2"
                  :display="' '"
                  :label="$t('Доверенность Клиента')" />
              <v-col
                  v-if="file.fileId"
                  cols="2"
                  class="ma-0 pa-0"
                  align-self="start">
                <text-view v-if="file.fileId" :display="file.fileName" />
                <btn-icon-tip
                    :disabled="isLoading"
                    icon="delete"
                    :tip="$t('Удалить документ')"
                    @click="deleteFile(file.fileId)" />
              </v-col>
              <v-col v-if="!file.fileId" cols="1" class="ma-0 pa-0">
                <typed-file-upload
                    :disabled="isLoading"
                    accept=".doc,.docx,.xls,.xlsx,.pdf"
                    :document-type="documentType"
                    :document-group="documentGroup"
                    :upload-method="uploadSingleBusinessFile" />
              </v-col>
            </v-col>
          </v-row>
          <v-row>
            <v-col class="text-left py-0">
              <validatable-checkbox
                  v-model="approve"
                  :readonly="innerLoading"
                  rules="required"
                  :label="conditionLabel" />
            </v-col>
          </v-row>
        </ValidationObserver>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn id="cancelConfirmBtn" color="primary" @click="cancel">
          {{ $t("Закрыть") }}
        </v-btn>
        <v-btn
            id="continueOperationBtn"
            color="primary"
            :disabled="!approve || innerLoading"
            @click.stop="openConfirmDialogOrSendRequest">
          {{ $t("Подать") }}
        </v-btn>
        <new-order-confirm-dialog
            v-if="newOrderConfirmDialog.show"
            :dialog-prop="newOrderConfirmDialog"
            @close="closeDialog('newOrderConfirmDialog')"
            @cancel="newOrderCanceled" />
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
  import Vue from 'vue'
  import GeneralError from '@/components/reused/common/GeneralError.vue'
  import ValidatableTextInput from '@/components/reused/ui/ValidatableTextInput.vue'
  import ValidatableDynamicTextInput from '@/components/reused/ui/ValidatableDynamicTextInput.vue'
  import TextView from '@/components/reused/ui/TextView.vue'
  import BtnIconTip from '@/components/reused/ui/BtnIconTip.vue'
  import TypedFileUpload from '@/components/reused/ui/TypedFileUpload.vue'
  import ValidatableCheckbox from '@/components/reused/ui/ValidatableCheckbox.vue'
  import ValidatableTextarea from '@/components/reused/ui/ValidatableTextarea.vue'
  import NewOrderConfirmDialog from './NewOrderConfirmDialog.vue'
  import {
    CURRENT_ORGANIZATION,
    CURRENT_USER,
    BUSINESS_FILES
  } from '@/constants/store'
  import {
    MAX_PREMIUM_STEP_COUNT,
    WARNING_PREMIUM_STEP_COUNT,
    MAX_FIX_PREMIUM_VALUE,
    MIN_FIX_PREMIUM_VALUE,
    MAX_DIFF_PREMIUM_VALUE,
    MIN_DIFF_PREMIUM_VALUE,
    DOCUMENT_TYPES,
    BUSINESS_DOCUMENT_GROUPS
  } from '@/constants/business'
  import { mapActions, mapState } from 'vuex'
  import getFileMixin from '@/utils/mixins/scoped/getFileMixin.js'

  export default {
    components: {
      GeneralError,
      ValidatableTextInput,
      ValidatableDynamicTextInput,
      TextView,
      BtnIconTip,
      ValidatableCheckbox,
      ValidatableTextarea,
      NewOrderConfirmDialog,
      TypedFileUpload
    },
    mixins: [getFileMixin('isLoading')],
    props: {
      dialogProp: {
        type: Object,
        default: () => {
          return {
            isFixPremium: true,
            currency: null,
            showAdditionalInfo: false,
            showZero: false,
            positions: null,
            callback: null,
            show: false
          }
        }
      }
    },
    data () {
      return {
        // разделено на два флага, чтобы контролировать обновление премий во время подачи запроса
        isLoading: false,
        isOrgLoading: false,
        success: false,
        approve: false,
        MAX_PREMIUM_STEP_COUNT,
        WARNING_PREMIUM_STEP_COUNT,
        MAX_FIX_PREMIUM_VALUE,
        MIN_FIX_PREMIUM_VALUE,
        MAX_DIFF_PREMIUM_VALUE,
        MIN_DIFF_PREMIUM_VALUE,
        documentType: DOCUMENT_TYPES.OFFER_CLIENT_PROXY,
        documentGroup: BUSINESS_DOCUMENT_GROUPS.OFFER_CLIENT_PROXY,
        stepCount: 0,
        order: {
          fileId: null,
          additionalInfo: null,
          premiums: [],
          clientOrganizationInn: null,
          clientOrganizationFullName: null
        },
        newOrderConfirmDialog: {
          show: false,
          stepCount: null,
          stepValue: null,
          premiums: [],
          organizationName: null,
          callback: null
        },
        isClient: false
      }
    },
    computed: {
      ...mapState(CURRENT_ORGANIZATION.NAMESPACE, {
        organization: state =>
          state[CURRENT_ORGANIZATION.STATE.ORGANIZATION] || {}
      }),
      ...mapState(CURRENT_USER.NAMESPACE, {
        currentUser: state => state || {}
      }),
      ...mapState(BUSINESS_FILES.NAMESPACE, {
        file: state => state[BUSINESS_FILES.STATE.CURRENT_FILE] || {}
      }),
      headers () {
        return [
          {
            text: this.$t('Предмет договора'),
            align: 'start',
            sortable: false,
            value: 'positionDescription'
          },
          {
            text: this.$t(
              this.dialogProp.isFixPremium
                ? 'Цена 1 (одной) углеродной единицы (с учетом НДС)'
                : 'Значение дифференциала'
            ),
            align: 'start',
            sortable: false,
            value: 'premium'
          }
        ]
      },
      conditionLabel () {
        if (this.isClient) {
          return this.$t(
            'Признаем, что поданное Уполномоченным представителем {0}, ОГРН: {1} {2} ценовое предложение является предложением Клиента участника: {3}, ИНН {4}, содержащим всю необходимую информацию для заключения Договора. Настоящее ценовое предложение является уведомлением о безусловном согласии на заключение Договора на условиях, содержащихся в настоящем ценовом предложении, в случае его акцепта Организатором.',
            [
              this.organization.organizationShortName,
              this.organization.ogrn,
              this.currentUser.contactPerson,
              this.order.clientOrganizationFullName,
              this.order.clientOrganizationInn
            ]
          )
        } else {
          return this.$t(
            'Признаем, что поданное {0} ценовое предложение в электронной форме является предложением {1}, ОГРН: {2}, содержащим всю необходимую информацию для заключения Договора. Настоящее ценовое предложение является уведомлением о безусловном согласии на заключение Договора на условиях, содержащихся в настоящем ценовом предложении, в случае ее акцепта Организатором.',
            [
              this.currentUser.contactPerson,
              this.organization.organizationShortName,
              this.organization.ogrn
            ]
          )
        }
      },
      startPremiumLabel () {
        return this.$t(
          this.dialogProp.isFixPremium
            ? 'Начальная цены (без шага)'
            : 'Стартовое значение дифференциала (без шага)'
        )
      },
      premiumLabel () {
        return this.$t(
          this.dialogProp.isFixPremium
            ? 'Цена 1 (одной) углеродной единицы (с учетом НДС)'
            : 'Значение дифференциала'
        )
      },
      showZero () {
        return !this.dialogProp.showZero || this.isLoading
      },
      innerLoading () {
        return this.isOrgLoading || this.isLoading
      }
    },
    watch: {
      'dialogProp.positions': {
        // immediate: true,
        // deep: true,
        handler (newVal, oldVal) {
          // ограничено изменение новой цены во время отправки заявки
          if (
            ((newVal?.length &&
            oldVal?.length &&
            newVal[0]?.premium !== !oldVal[0]?.premium) ||
              (newVal?.length && !oldVal?.length)) &&
            !this.isLoading
          ) {
            this.initPremiums(newVal)
          }
        }
      },
      showZero: {
        immediate: true,
        handler (newVal) {
          // ограничено изменение новой цены во время отправки заявки
          if (!newVal && this.stepCount === 0 && !this.isLoading) {
            this.stepCount = 1
          }
        }
      }
    },
    created () {
      if (!this.organization.organizationId) {
        this.getCurrentOrganization()
      }
      this.initPremiums(this.dialogProp.positions)
    },
    beforeDestroy () {
      this.clearSingleBusinessFileAction()
      this.clearCurrentOrganizationAction()
    },
    methods: {
      ...mapActions(CURRENT_ORGANIZATION.NAMESPACE, {
        getCurrentOrganizationAction:
          CURRENT_ORGANIZATION.ACTIONS.GET_CURRENT_ORGANIZATION,
        clearCurrentOrganizationAction:
          CURRENT_ORGANIZATION.ACTIONS.CLEAR_CURRENT_ORGANIZATION
      }),
      ...mapActions(BUSINESS_FILES.NAMESPACE, {
        uploadSingleBusinessFileAction:
          BUSINESS_FILES.ACTIONS.UPLOAD_SINGLE_BUSINESS_FILE,
        deleteBusinessFileAction: BUSINESS_FILES.ACTIONS.DELETE_BUSINESS_FILE,
        clearSingleBusinessFileAction:
          BUSINESS_FILES.ACTIONS.CLEAR_SINGLE_BUSINESS_FILE
      }),
      uploadSingleBusinessFile: async function (
        form,
        documentType,
        documentGroup
      ) {
        // try catch не нужен (если ошибка, обработаем в месте вызова метода)

        if (this.isAdmin) {
          form.append('creatorId', this.dialogProp.lot.creatorId)
        }
        try {
          this.isLoading = true
          await this.uploadSingleBusinessFileAction({
            form,
            documentType,
            documentGroup
          })
          this.order.fileId = this.file.fileId
        } finally {
          this.isLoading = false
        }
      },
      deleteFile: async function (fileId) {
        try {
          this.isLoading = true
          await this.deleteBusinessFileAction({ fileId })
          await this.clearSingleBusinessFileAction()
          this.order.fileId = null
          this.isLoading = false
        } catch (error) {
          this.isLoading = false
          this.throwIfUnknownError(error)
        }
      },
      initPremiums: function (nextPremiums) {
        const vm = this
        if (nextPremiums && nextPremiums.length) {
          if (!this.order.premiums.length) {
            this.order.premiums = nextPremiums.map(p => ({ ...p }))
          } else {
            const oldPremium = this.order.premiums[0]
            const newPremium = nextPremiums.find(
              p => p.positionId === oldPremium.positionId
            )

            if (oldPremium.premium >= newPremium.premium) {
              const stepCount = Number.parseInt(
                (oldPremium.premium - newPremium.premium) /
                  vm.dialogProp.premiumStep +
                  1
              )
              vm.stepCount = stepCount
            } else if (!vm.newOrderConfirmDialog.show) {
              this.order.premiums = nextPremiums.map(p => ({ ...p }))
              vm.stepCount = 1
              vm.approve = false
            }
          }
        }
      },
      newOrderCanceled: function () {
        this.initPremiums(this.dialogProp.positions)
      },
      openConfirmDialogOrSendRequest: async function () {
        await this.hideErrorAction()
        try {
          const result = await this.$refs.order.validate()
          if (!result) {
            return
          }
          if (!this.order.premiums.length) {
            throw new Vue.errorsTypes.ValidationError(
              this.$t(
                'Заявка сформирована некорректно. Обратитесь к администратору.'
              )
            )
          }
          this.isLoading = true
          await this.continueOperationThrowErrors()
        } catch (error) {
          this.throwIfUnknownError(error)
        } finally {
          this.isLoading = false
        }
      },
      getCurrentOrganization: async function () {
        this.isOrgLoading = true
        try {
          await this.getCurrentOrganizationAction()
        } catch (error) {
          this.throwIfNotServerError(error)
        } finally {
          this.isOrgLoading = false
        }
      },
      continueOperationThrowErrors: async function () {
        this.success = false
        if (this.dialogProp.callback) {
          await this.dialogProp.callback({
            ...this.order,
            premiums: this.order.premiums.map(p => ({
              positionId: p.positionId,
              premium: p.premium
            }))
          })
        }
        this.success = true
        this.cancel()
      },
      cancel: function () {
        this.$emit('close')
      },
      clearClientInfo: function (value) {
        if (value === false) {
          this.order.clientOrganizationFullName = null
          this.order.clientOrganizationInn = null
          if (this.file.fileId) {
            this.deleteFile(this.file.fileId)
          }
        }
      }
    }
  }
</script>
